import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Contract } from '@/types';
import { SearchFilterBar } from '@/components/base/SearchFilterBar';

interface ContractFiltersProps {
  contracts: Contract[];
  farmers: { id: string; name: string }[];
  seasons: { id: string; name: string }[];
  onFilterChange: (filtered: Contract[]) => void;
}

const QUICK_FILTERS = [
  { id: '', label: 'Tất cả' },
  { id: 'Đang hiệu lực', label: 'Hiệu lực' },
  { id: 'Sắp hết hạn', label: 'Sắp hết hạn' },
  { id: 'Hết hạn', label: 'Hết hạn' },
];

export default function ContractFilters({ contracts, farmers, seasons, onFilterChange }: ContractFiltersProps) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [farmerId, setFarmerId] = useState('');
  const [seasonId, setSeasonId] = useState('');

  // "Sắp hết hạn" is its own flag now, not a status value a real contract can
  // hold (Feedback 08 A2) — the legacy mock data still uses it as a literal
  // status, so match either shape here and in `filter` below.
  const matchesQuickFilter = useCallback(
    (contract: Contract, filterId: string) => (filterId === 'Sắp hết hạn' ? Boolean(contract.expiringSoon) || contract.status === 'Sắp hết hạn' : contract.status === filterId),
    [],
  );

  const counts = useMemo(() => Object.fromEntries(QUICK_FILTERS.map((item) => [
    item.id,
    item.id ? contracts.filter((contract) => matchesQuickFilter(contract, item.id) && !contract.isDeleted).length : contracts.filter((contract) => !contract.isDeleted).length,
  ])), [contracts, matchesQuickFilter]);

  const filter = useCallback(() => {
    const query = search.trim().toLowerCase();
    onFilterChange(contracts.filter((contract) => {
      if (contract.isDeleted) return false;
      const farmerName = farmers.find((farmer) => farmer.id === contract.farmerId)?.name || '';
      if (query && !`${contract.soHopDong} ${contract.doiTac || ''} ${farmerName}`.toLowerCase().includes(query)) return false;
      if (status && !matchesQuickFilter(contract, status)) return false;
      if (farmerId && contract.farmerId !== farmerId) return false;
      if (seasonId && !contract.seasonIds?.includes(seasonId)) return false;
      return true;
    }));
  }, [contracts, farmerId, farmers, matchesQuickFilter, onFilterChange, search, seasonId, status]);

  useEffect(() => { filter(); }, [filter]);

  return (
    <SearchFilterBar
      value={search}
      onChange={setSearch}
      placeholder="Tìm hợp đồng hoặc nông hộ"
      selected={status}
      onSelect={setStatus}
      options={QUICK_FILTERS.map((item) => ({ value: item.id, label: item.label, count: counts[item.id] || 0 }))}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <select value={farmerId} onChange={(event) => setFarmerId(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm">
          <option value="">Tất cả nông hộ</option>
          {farmers.map((farmer) => <option key={farmer.id} value={farmer.id}>{farmer.name}</option>)}
        </select>
        <select value={seasonId} onChange={(event) => setSeasonId(event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm">
          <option value="">Tất cả mùa vụ</option>
          {seasons.map((season) => <option key={season.id} value={season.id}>{season.name}</option>)}
        </select>
      </div>
    </SearchFilterBar>
  );
}
